import React, { useState, useMemo, useCallback } from "react"
import { Card, CardBody } from "reactstrap"


import GradeHeader from "./components/GradeHeader"
import GradeGrid from "./components/GradeGrid"
import AttendanceModal from "./components/AttendanceModal"

import { ContentLoader } from "../../components/Common"
import { useGradeData } from "./hooks/useGradeData"
import { getStartOfWeek } from "../../utils/date"

/**
 * Página Principal da Grade de Aulas (Operacional).
 * Consome sessões reais do Firebase via useGradeData.
 */
const Grade = () => {

  // Parâmetros de Data e Visão
  const [referenceDate, setReferenceDate] = useState(new Date())
  const [view, setView] = useState("week")
  const [turn, setTurn] = useState("all")

  // Busca sessões reais da base de dados
  const { sessions, loading: loadingData, refresh } = useGradeData({ referenceDate, view })

  // Cálculo do Início da Semana (memoizado para evitar re-render)
  const weekStart = useMemo(() => getStartOfWeek(referenceDate), [referenceDate])

  // Estados Locais de Seleção
  const [selectedScheduleId, setSelectedScheduleId] = useState(null)
  const [selectedScheduleKey, setSelectedScheduleKey] = useState(null)

  // Controle do Modal de Chamada
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false)
  const [activeAttendanceSchedule, setActiveAttendanceSchedule] = useState(null)



  // Handler de Seleção na Grade (memoizado p/ evitar re-render do GradeGrid)
  const handleSelectSchedule = useCallback((schedule, iso) => {
    const id = String(schedule?.id || "")
    if (!id) return
    const key = iso ? `${id}|${iso}` : id

    // Seleção para destaque (shadow)
    setSelectedScheduleId(id)
    setSelectedScheduleKey(key)

    // Abre o modal de chamada para a sessão clicada
    setActiveAttendanceSchedule(schedule)
    setIsAttendanceOpen(true)
  }, [])

  if (loadingData && sessions.length === 0) {
    return <ContentLoader loading={true} message="Carregando grade..." />
  }

  return (
    <div
      className="h-100 w-100 pb-5"
      onClick={() => {
        setSelectedScheduleId(null);
        setSelectedScheduleKey(null);
      }}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Card className="mb-4">
          <CardBody className="pb-2">
            <GradeHeader
              turn={turn}
              onTurnChange={setTurn}
              view={view}
              onViewChange={setView}
              referenceDate={referenceDate}
              onReferenceDateChange={setReferenceDate}
            />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardBody>
          {!loadingData && sessions.length === 0 && (
            <div className="alert alert-info mb-3">
              <i className="mdi mdi-information me-2"></i>
              <strong>Nenhuma sessão encontrada.</strong>
              <p className="mb-0 mt-2">
                Cadastre turmas no módulo Gerencial → Turmas para que as sessões apareçam automaticamente na grade.
              </p>
            </div>
          )}
          <GradeGrid
            turn={turn}
            view={view}
            referenceDate={referenceDate}
            weekStart={weekStart}
            schedules={sessions}
            loading={loadingData}
            onSelectSchedule={handleSelectSchedule}
            selectedScheduleId={selectedScheduleId}
            selectedScheduleKey={selectedScheduleKey}
          />
        </CardBody>
      </Card>

      {/* Modal de Controle de Presença */}
      <AttendanceModal
        isOpen={isAttendanceOpen}
        onClose={() => setIsAttendanceOpen(false)}
        schedule={activeAttendanceSchedule}
        onAttendanceSaved={refresh}
      />
    </div>
  )
}

export default Grade
